/**
 * Page Builder section renderers (Chakra) — the crosure-lite-studio port of
 * crosure-studio's Tailwind `/p/[slug]` reference renderer. One component per
 * PageSectionType; config keys match the reference 1:1 so the same Studio
 * config drives both apps.
 *
 * `form_embed` renders a CTA linking to the form's own journey route
 * (`/forms/{formCode}`) rather than embedding the multi-step wizard inline —
 * lite-studio forms are full quote→pay journeys (FormPage), not cleanly
 * embeddable as a marketing-page section. See the page-builder-lite-renderer
 * plan's form_embed note.
 */
import React from 'react'
import TenantComponentSection, {
	type ResolvedTenantProduct,
} from '@/components/page/TenantComponentSection'
import NextLink from 'next/link'
import { useTranslations } from 'next-intl'
import {
	Box,
	Button,
	Container,
	Divider,
	Flex,
	Heading,
	Image,
	SimpleGrid,
	Stack,
	Text,
} from '@chakra-ui/react'
import type { StudioArticleCard, StudioPageSection } from '@/lib/studioApi'

type Cfg = Record<string, unknown>
const s = (v: unknown, d = ''): string =>
	v === undefined || v === null ? d : String(v)
const arr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : [])

/**
 * Shared section styling — mirrors _SectionStyleMixin on the backend and the
 * identically-named helpers in crosure-studio's page-sections/index.tsx.
 * Every value is optional; unset means "use the renderer's built-in styling",
 * so sections saved before these controls existed render unchanged.
 */
const MAX_W: Record<string, string> = {
	narrow: '2xl',
	normal: '4xl',
	wide: '6xl',
	full: 'full',
}

/**
 * Readable foreground for an author-chosen background, when none is set.
 * Hero previously defaulted to #ffffff unconditionally, so a light background
 * produced white-on-white. An explicit text_color still always wins.
 */
export function readableTextColor(bg: string): string {
	const hex = String(bg || '')
		.trim()
		.replace('#', '')
	if (!/^[0-9a-fA-F]{6}$/.test(hex) && !/^[0-9a-fA-F]{3}$/.test(hex)) return ''
	const full =
		hex.length === 3
			? hex
					.split('')
					.map(c => c + c)
					.join('')
			: hex
	const r = parseInt(full.slice(0, 2), 16)
	const g = parseInt(full.slice(2, 4), 16)
	const b = parseInt(full.slice(4, 6), 16)
	// Rec. 709 relative luminance.
	const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
	return lum > 0.55 ? '#111827' : '#ffffff'
}

interface SectionBoxProps {
	bg?: string
	color?: string
	/** Chakra applies props in source order, so a hardcoded `py` appearing
	 *  after `pt`/`pb` silently wins. The section's default py therefore has
	 *  to come from here too, not from a literal on the Box. */
	py?: number
	/** Explicit px strings, NOT numbers: Chakra reads a bare number as a
	 *  spacing-scale token (pt={60} means theme.space[60], which doesn't
	 *  exist), so the configured pixel padding silently did nothing. */
	pt?: string
	pb?: string
	textAlign?: 'left' | 'center' | 'right'
	maxW: string
	id?: string
}

/** The style values a single breakpoint resolves to. */
function resolveStyleValues(config: Cfg, defaultMaxW: string) {
	const bg = s(config.bg_color)
	const explicit = s(config.text_color)
	const color = explicit || (bg ? readableTextColor(bg) : '')
	const align = s(config.align)
	return {
		bg: bg || undefined,
		color: color || undefined,
		pt: config.padding_top ? `${Number(config.padding_top)}px` : undefined,
		pb: config.padding_bottom
			? `${Number(config.padding_bottom)}px`
			: undefined,
		textAlign: (['left', 'center', 'right'].includes(align)
			? align
			: undefined) as SectionBoxProps['textAlign'],
		maxW: MAX_W[s(config.max_width)] ?? defaultMaxW,
	}
}

/**
 * Per-breakpoint style overrides (PLANS.md Tier 4.5).
 *
 * They live under a reserved `responsive` key on the section config — plain
 * JSON, so no migration and older sections are unaffected. The base config is
 * the desktop value; `responsive.mobile` and `responsive.tablet` override it.
 *
 * Overrides are merged OVER the base config before resolving, so a breakpoint
 * that only sets `padding_top` inherits everything else (and a derived value
 * like the readable text colour is recomputed against the overridden bg).
 *
 * Emitted mobile-first, matching Chakra: `base` is the mobile value, `md` the
 * tablet value, `lg` restores desktop. With no `responsive` key the props are
 * plain scalars exactly as before — this cannot change an existing page.
 */
export function sectionBoxProps(
	config: Cfg,
	defaultMaxW = '4xl',
	defaultPy = 12
): SectionBoxProps {
	const responsive = (config.responsive ?? {}) as {
		mobile?: Cfg
		tablet?: Cfg
	}
	const hasMobile = Boolean(
		responsive.mobile && Object.keys(responsive.mobile).length
	)
	const hasTablet = Boolean(
		responsive.tablet && Object.keys(responsive.tablet).length
	)

	const desktop = resolveStyleValues(config, defaultMaxW)

	if (!hasMobile && !hasTablet) {
		return {
			...desktop,
			py: defaultPy,
			id: s(config.anchor_id) || undefined,
		}
	}

	const mobile = hasMobile
		? resolveStyleValues({ ...config, ...responsive.mobile }, defaultMaxW)
		: desktop
	const tablet = hasTablet
		? resolveStyleValues({ ...config, ...responsive.tablet }, defaultMaxW)
		: desktop

	// Only widen a prop into a breakpoint object when the value actually
	// differs — an unchanged prop stays a scalar, keeping the DOM output (and
	// any snapshot tests) as close to before as possible.
	const at = <K extends keyof typeof desktop>(key: K) => {
		const d = desktop[key]
		const m = mobile[key]
		const t = tablet[key]
		if (m === d && t === d) return d
		return { base: m, md: t, lg: d } as unknown as typeof d
	}

	return {
		bg: at('bg'),
		color: at('color'),
		py: defaultPy,
		pt: at('pt'),
		pb: at('pb'),
		textAlign: at('textAlign'),
		maxW: at('maxW'),
		id: s(config.anchor_id) || undefined,
	}
}

function localeHref(locale: string, href: string): string {
	if (!href) return '#'
	if (href.startsWith('#')) return href
	if (
		/^https?:\/\//.test(href) ||
		href.startsWith('mailto:') ||
		href.startsWith('tel:')
	)
		return href
	const routeLocale = locale.split('-')[0].toLowerCase()
	const target = new URL(
		href.startsWith('/') ? href : `/${href}`,
		'https://local'
	)
	target.searchParams.set('lang', locale)
	const isAlreadyLocalized =
		target.pathname === `/${routeLocale}` ||
		target.pathname.startsWith(`/${routeLocale}/`)
	const pathname = isAlreadyLocalized
		? target.pathname
		: `/${routeLocale}${target.pathname}`
	return `${pathname}${target.search}${target.hash}`
}

function ctaHref(
	config: Cfg,
	locale: string,
	formKey = 'cta_form_code',
	linkKey = 'cta_link'
): string {
	if (config[formKey]) return localeHref(locale, `/forms/${s(config[formKey])}`)
	if (config[linkKey]) return localeHref(locale, s(config[linkKey]))
	return ''
}

// --- Hero ------------------------------------------------------------------
function HeroSection({ config, locale }: { config: Cfg; locale: string }) {
	const bg = s(config.bg_color, '#1e40af')
	// Was unconditionally '#ffffff' — any light background was unreadable.
	const fg = s(config.text_color) || readableTextColor(bg)
	const imageUrl = config.image_url ? s(config.image_url) : undefined
	const href = ctaHref(config, locale)
	const box = sectionBoxProps(config, '4xl')
	return (
		<Box
			id={box.id}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
			position='relative'
			px={{ base: 4, sm: 8 }}
			py={{ base: 20, sm: 28 }}
			bg={bg}
			color={fg}
			bgImage={imageUrl ? `url(${imageUrl})` : undefined}
			bgSize='cover'
			bgPosition='center'
			overflow='hidden'
		>
			{imageUrl && <Box position='absolute' inset={0} bg='blackAlpha.500' />}
			<Container maxW={box.maxW} position='relative' textAlign='center'>
				{Boolean(config.heading) && (
					<Heading size='2xl' lineHeight='1.1' color={fg}>
						{s(config.heading)}
					</Heading>
				)}
				{Boolean(config.subheading) && (
					<Text
						mt={4}
						fontSize={{ base: 'lg', sm: 'xl' }}
						opacity={0.9}
						color={fg}
					>
						{s(config.subheading)}
					</Text>
				)}
				{Boolean(config.cta_text) && href && (
					<Box mt={8}>
						<Button
							as={NextLink}
							href={href}
							size='lg'
							bg='white'
							color={bg}
							_hover={{ opacity: 0.9 }}
						>
							{s(config.cta_text)}
						</Button>
					</Box>
				)}
			</Container>
		</Box>
	)
}

// --- Markdown / Rich text --------------------------------------------------
function MarkdownSection({ config }: { config: Cfg }) {
	const html = s(config.content_html)
	const content = s(config.content)
	if (!html && !content) return null
	const box = sectionBoxProps(config, '3xl')
	return (
		<Box
			id={box.id}
			px={{ base: 4, sm: 8 }}
			py={12}
			bg={box.bg}
			color={box.color}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				{html ? (
					// Converted from markdown and sanitized SERVER-SIDE on save
					// (nh3, same allowlist as Article.body). Studio is the
					// boundary — never re-trust or re-clean here. Inline HTML the
					// author wrote renders instead of being printed literally.
					<Box
						fontSize='sm'
						lineHeight='1.7'
						sx={{
							'h1, h2, h3': { fontWeight: 'bold', mt: 4, mb: 2 },
							h1: { fontSize: '2xl' },
							h2: { fontSize: 'xl' },
							h3: { fontSize: 'lg' },
							p: { mb: 3 },
							'ul, ol': { pl: 6, mb: 3 },
							li: { mb: 1 },
							a: { color: 'blue.600', textDecoration: 'underline' },
							table: { width: '100%', mb: 3 },
							'th, td': { borderWidth: '1px', p: 2 },
							blockquote: {
								borderLeftWidth: '4px',
								pl: 4,
								fontStyle: 'italic',
								my: 3,
							},
							img: { maxW: '100%', borderRadius: 'md', my: 3 },
						}}
						dangerouslySetInnerHTML={{ __html: html }}
					/>
				) : (
					// Rows saved before server-side rendering existed have no
					// content_html; show the source rather than nothing. They pick
					// up real rendering on their next save.
					<Text whiteSpace='pre-wrap' fontSize='sm' lineHeight='1.7'>
						{content}
					</Text>
				)}
			</Container>
		</Box>
	)
}

// --- CTA banner ------------------------------------------------------------
function CtaSection({ config, locale }: { config: Cfg; locale: string }) {
	const bg = s(config.bg_color, '#0f172a')
	const href = ctaHref(config, locale, 'button_form_code', 'button_link')
	const box = sectionBoxProps(config, '3xl', 16)
	return (
		<Box
			id={box.id}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
			bg={box.bg || bg}
		>
			<Container maxW={box.maxW} textAlign='center'>
				{Boolean(config.heading) && (
					<Heading size='lg' color='white'>
						{s(config.heading)}
					</Heading>
				)}
				{Boolean(config.subheading) && (
					<Text mt={3} fontSize='lg' color='whiteAlpha.800'>
						{s(config.subheading)}
					</Text>
				)}
				{Boolean(config.button_text) && href && (
					<Box mt={8}>
						<Button
							as={NextLink}
							href={href}
							size='lg'
							bg='white'
							color={bg}
							_hover={{ opacity: 0.9 }}
						>
							{s(config.button_text)}
						</Button>
					</Box>
				)}
			</Container>
		</Box>
	)
}

// --- Stats -----------------------------------------------------------------
function StatsSection({ config }: { config: Cfg }) {
	const items = arr<Cfg>(config.items)
	const box = sectionBoxProps(config, '5xl')
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				{Boolean(config.title) && (
					<Heading size='lg' textAlign='center' mb={8}>
						{s(config.title)}
					</Heading>
				)}
				<SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={6}>
					{items.map((item, i) => (
						<Box
							key={i}
							borderWidth='1px'
							borderColor='gray.100'
							borderRadius='xl'
							px={6}
							py={8}
							textAlign='center'
							boxShadow='sm'
						>
							<Text fontSize='4xl' fontWeight='extrabold' color='primary.500'>
								{s(item.value)}
								{Boolean(item.unit) && (
									<Text
										as='span'
										fontSize='2xl'
										fontWeight='medium'
										color='gray.500'
										ml={1}
									>
										{s(item.unit)}
									</Text>
								)}
							</Text>
							<Text mt={2} fontSize='sm' fontWeight='medium' color='gray.500'>
								{s(item.label)}
							</Text>
						</Box>
					))}
				</SimpleGrid>
			</Container>
		</Box>
	)
}

// --- Separator -------------------------------------------------------------
function SeparatorSection({ config }: { config: Cfg }) {
	const height = Number(config.height_px ?? 40)
	const showLine = Boolean(config.show_line ?? false)
	const box = sectionBoxProps(config, '4xl')
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
			height={`${height}px`}
			display={showLine ? 'flex' : undefined}
			alignItems='center'
			px={{ base: 4, sm: 8 }}
		>
			{showLine && <Divider borderColor='gray.200' />}
		</Box>
	)
}

// --- FAQ -------------------------------------------------------------------
function FaqSection({ config }: { config: Cfg }) {
	const items = arr<{ q: string; a: string }>(config.items)
	const box = sectionBoxProps(config, '3xl')
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				{Boolean(config.title) && (
					<Heading size='lg' mb={8}>
						{s(config.title)}
					</Heading>
				)}
				<Box
					borderWidth='1px'
					borderColor='gray.100'
					borderRadius='xl'
					overflow='hidden'
				>
					{items.map((item, i) => (
						<Box
							as='details'
							key={i}
							px={5}
							py={4}
							borderTopWidth={i === 0 ? 0 : '1px'}
							borderColor='gray.100'
						>
							<Box
								as='summary'
								cursor='pointer'
								fontSize='sm'
								fontWeight='medium'
							>
								{item.q}
							</Box>
							<Text mt={3} fontSize='sm' color='gray.600' lineHeight='1.7'>
								{item.a}
							</Text>
						</Box>
					))}
				</Box>
			</Container>
		</Box>
	)
}

// --- Testimonials ----------------------------------------------------------
function TestimonialsSection({ config }: { config: Cfg }) {
	const items = arr<{
		quote: string
		author: string
		role: string
		avatar_url: string
	}>(config.items)
	const cols = Number(config.columns ?? 3)
	const box = sectionBoxProps(config, '6xl')
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				{Boolean(config.title) && (
					<Heading size='lg' textAlign='center' mb={8}>
						{s(config.title)}
					</Heading>
				)}
				<SimpleGrid
					columns={{ base: 1, sm: Math.min(2, cols), lg: cols }}
					spacing={6}
				>
					{items.map((item, i) => (
						<Stack
							key={i}
							borderWidth='1px'
							borderColor='gray.100'
							borderRadius='2xl'
							p={6}
							boxShadow='sm'
							justify='space-between'
						>
							<Text fontSize='sm' color='gray.600' lineHeight='1.7'>
								&ldquo;{item.quote}&rdquo;
							</Text>
							<Flex mt={4} align='center' gap={3}>
								{Boolean(item.avatar_url) && (
									<Image
										src={item.avatar_url}
										alt={item.author}
										boxSize='36px'
										borderRadius='full'
										objectFit='cover'
									/>
								)}
								<Box>
									<Text fontSize='sm' fontWeight='semibold'>
										{item.author}
									</Text>
									{Boolean(item.role) && (
										<Text fontSize='xs' color='gray.500'>
											{item.role}
										</Text>
									)}
								</Box>
							</Flex>
						</Stack>
					))}
				</SimpleGrid>
			</Container>
		</Box>
	)
}

// --- Image + Text ----------------------------------------------------------
function ImageTextSection({ config, locale }: { config: Cfg; locale: string }) {
	const imageLeft = s(config.image_position, 'right') === 'left'
	const href = config.cta_link ? localeHref(locale, s(config.cta_link)) : ''
	const box = sectionBoxProps(config, '5xl')
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				<Flex
					direction={{ base: 'column', lg: imageLeft ? 'row-reverse' : 'row' }}
					gap={8}
					align='center'
				>
					{Boolean(config.image_url) && (
						<Image
							src={s(config.image_url)}
							alt={s(config.heading)}
							maxW={{ base: '100%', lg: '50%' }}
							borderRadius='2xl'
							objectFit='cover'
							boxShadow='md'
						/>
					)}
					<Stack flex='1' spacing={4}>
						{Boolean(config.heading) && (
							<Heading size='lg'>{s(config.heading)}</Heading>
						)}
						{Boolean(config.body) && (
							<Text color='gray.600' lineHeight='1.7'>
								{s(config.body)}
							</Text>
						)}
						{Boolean(config.cta_text) && href && (
							<Box>
								<Button
									as={NextLink}
									href={href}
									colorScheme='primary'
									size='md'
								>
									{s(config.cta_text)}
								</Button>
							</Box>
						)}
					</Stack>
				</Flex>
			</Container>
		</Box>
	)
}

// --- Logos -----------------------------------------------------------------
function LogosSection({ config, locale }: { config: Cfg; locale: string }) {
	const logos = arr<{ name: string; image_url: string; link_url: string }>(
		config.logos
	)
	const height = Number(config.logo_height_px ?? 48)
	const box = sectionBoxProps(config, '5xl', 10)
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				{Boolean(config.label) && (
					<Text
						mb={6}
						textAlign='center'
						fontSize='sm'
						fontWeight='medium'
						textTransform='uppercase'
						letterSpacing='wide'
						color='gray.500'
					>
						{s(config.label)}
					</Text>
				)}
				<Flex wrap='wrap' align='center' justify='center' gap={8}>
					{logos.map((logo, i) =>
						logo.link_url ? (
							<NextLink
								key={i}
								href={localeHref(locale, logo.link_url)}
								target='_blank'
								rel='noopener noreferrer'
							>
								<Image
									src={logo.image_url}
									alt={logo.name}
									height={`${height}px`}
									objectFit='contain'
									opacity={0.6}
									_hover={{ opacity: 1 }}
								/>
							</NextLink>
						) : (
							<Image
								key={i}
								src={logo.image_url}
								alt={logo.name}
								height={`${height}px`}
								objectFit='contain'
								opacity={0.6}
							/>
						)
					)}
				</Flex>
			</Container>
		</Box>
	)
}

// --- Product showcase ------------------------------------------------------
function ProductShowcaseSection({
	config,
	product,
	locale,
}: {
	config: Cfg
	product: Cfg | null
	locale: string
}) {
	const t = useTranslations('pageBuilder')
	if (!product) return null
	const coverages = arr<Cfg>(product.coverages)
	const href = config.cta_form_code
		? localeHref(locale, `/forms/${s(config.cta_form_code)}`)
		: ''
	const box = sectionBoxProps(config, '4xl')
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				<Box
					borderWidth='1px'
					borderColor='gray.100'
					borderRadius='2xl'
					p={8}
					boxShadow='sm'
				>
					<Flex wrap='wrap' justify='space-between' gap={4} align='flex-start'>
						<Box>
							<Heading size='lg'>{s(product.name)}</Heading>
							{Boolean(product.description) && (
								<Text mt={1} color='gray.600'>
									{s(product.description)}
								</Text>
							)}
						</Box>
						{config.show_premium !== false && Boolean(product.base_premium) && (
							<Box textAlign='right'>
								<Text
									fontSize='xs'
									fontWeight='medium'
									textTransform='uppercase'
									color='gray.500'
								>
									{t('from')}
								</Text>
								<Text fontSize='3xl' fontWeight='bold' color='primary.500'>
									{s(product.base_currency)} {s(product.base_premium)}
								</Text>
							</Box>
						)}
					</Flex>
					{config.show_coverages !== false && coverages.length > 0 && (
						<Box mt={8}>
							<SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
								{coverages.slice(0, 6).map((cov, i) => (
									<Flex
										key={i}
										gap={3}
										borderWidth='1px'
										borderColor='gray.100'
										bg='gray.50'
										borderRadius='lg'
										px={4}
										py={3}
										align='flex-start'
									>
										<Text color='green.500'>✓</Text>
										<Box>
											<Text fontSize='sm' fontWeight='medium'>
												{s(cov.name)}
											</Text>
											{Boolean(cov.description) && (
												<Text fontSize='xs' color='gray.500'>
													{s(cov.description)}
												</Text>
											)}
										</Box>
									</Flex>
								))}
							</SimpleGrid>
						</Box>
					)}
					{config.show_cta !== false && Boolean(config.cta_text) && href && (
						<Box mt={8}>
							<Button as={NextLink} href={href} colorScheme='primary' size='lg'>
								{s(config.cta_text)}
							</Button>
						</Box>
					)}
				</Box>
			</Container>
		</Box>
	)
}

// --- Coverage grid ---------------------------------------------------------
function CoverageGridSection({
	config,
	product,
}: {
	config: Cfg
	product: Cfg | null
}) {
	const t = useTranslations('pageBuilder')
	const coverages = arr<Cfg>(product?.coverages)
	const cols = Number(config.columns ?? 3)
	const box = sectionBoxProps(config, '5xl')
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				{Boolean(config.title) && (
					<Heading size='lg' textAlign='center' mb={8}>
						{s(config.title)}
					</Heading>
				)}
				{coverages.length === 0 ? (
					<Text textAlign='center' fontSize='sm' color='gray.500'>
						{t('noCoverages')}
					</Text>
				) : (
					<SimpleGrid columns={{ base: 1, sm: 2, lg: cols }} spacing={4}>
						{coverages.map((cov, i) => (
							<Box
								key={i}
								borderWidth='1px'
								borderColor='gray.100'
								borderRadius='xl'
								p={5}
								boxShadow='sm'
							>
								<Flex
									mb={3}
									boxSize='40px'
									align='center'
									justify='center'
									borderRadius='lg'
									bg='primary.50'
									color='primary.500'
									fontSize='lg'
								>
									✓
								</Flex>
								<Heading size='sm'>{s(cov.name)}</Heading>
								{Boolean(cov.description) && (
									<Text mt={1} fontSize='sm' color='gray.600'>
										{s(cov.description)}
									</Text>
								)}
								{Boolean(
									cov.display_value ??
									cov.default_sum_insured ??
									cov.sum_insured
								) && (
									<Text
										mt={3}
										fontSize='xs'
										fontWeight='medium'
										color='gray.500'
									>
										{t('sumInsured')}:{' '}
										{s(
											cov.display_value ??
												cov.default_sum_insured ??
												cov.sum_insured
										)}
									</Text>
								)}
							</Box>
						))}
					</SimpleGrid>
				)}
			</Container>
		</Box>
	)
}

// --- Form embed (CTA to the form journey) ----------------------------------
function FormEmbedSection({ config, locale }: { config: Cfg; locale: string }) {
	const t = useTranslations('pageBuilder')
	const formCode = s(config.form_code)
	if (!formCode) return null
	const href = localeHref(locale, `/forms/${formCode}`)
	const box = sectionBoxProps(config, '2xl')
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW} textAlign='center'>
				{config.show_title !== false && Boolean(config.title) && (
					<Heading size='lg' mb={2}>
						{s(config.title)}
					</Heading>
				)}
				{Boolean(config.description) && (
					<Text mb={8} color='gray.600'>
						{s(config.description)}
					</Text>
				)}
				<Button as={NextLink} href={href} colorScheme='primary' size='lg'>
					{s(config.cta_text, t('getQuote'))}
				</Button>
			</Container>
		</Box>
	)
}

function BlogListingSection({
	config,
	articles,
	locale,
}: {
	config: Cfg
	articles: StudioArticleCard[]
	locale: string
}) {
	const columns = Math.min(Math.max(Number(config.columns ?? 3), 1), 4)
	// Articles are fetched server-side by the page (the dispatcher is sync),
	// already filtered/ordered per this section's config. An empty list means
	// the tenant has published nothing yet — render nothing rather than an
	// empty grid with a heading floating above it.
	if (!articles.length) return null
	const box = sectionBoxProps(config, '6xl')
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				{Boolean(config.title) && (
					<Heading size='lg' mb={8} textAlign='center'>
						{s(config.title)}
					</Heading>
				)}
				<SimpleGrid
					columns={{ base: 1, md: Math.min(columns, 2), lg: columns }}
					spacing={6}
				>
					{articles.map(article => (
						<NextLink
							key={article.slug}
							href={localeHref(locale, `/articles/${article.slug}`)}
						>
							<Box
								borderWidth='1px'
								borderRadius='lg'
								overflow='hidden'
								transition='box-shadow 0.2s'
								_hover={{ boxShadow: 'md' }}
							>
								{Boolean(article.hero_image_url) && (
									<Image
										src={article.hero_image_url}
										alt={article.title}
										w='100%'
										h='180px'
										objectFit='cover'
									/>
								)}
								<Stack p={5} spacing={2}>
									<Heading size='sm'>{article.title}</Heading>
									{Boolean(article.meta_description) && (
										<Text fontSize='sm' color='gray.600' noOfLines={3}>
											{article.meta_description}
										</Text>
									)}
									<Text fontSize='xs' color='gray.500'>
										{[
											article.author_display_name,
											article.published_at
												? new Date(article.published_at).toLocaleDateString()
												: '',
										]
											.filter(Boolean)
											.join(' · ')}
									</Text>
								</Stack>
							</Box>
						</NextLink>
					))}
				</SimpleGrid>
			</Container>
		</Box>
	)
}

function HowItWorksSection({ config }: { config: Cfg }) {
	const steps = arr<{ title: string; description: string; icon: string }>(
		config.steps
	)
	if (!steps.length) return null
	const box = sectionBoxProps(config, '5xl')
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				{Boolean(config.title) && (
					<Heading size='lg' mb={10} textAlign='center'>
						{s(config.title)}
					</Heading>
				)}
				<SimpleGrid
					columns={{ base: 1, md: Math.min(steps.length, 4) }}
					spacing={8}
				>
					{steps.map((step, i) => (
						<Stack key={i} spacing={3} textAlign='center' align='center'>
							<Flex
								align='center'
								justify='center'
								borderRadius='full'
								boxSize='48px'
								bg='primary.500'
								color='white'
								fontWeight='bold'
							>
								{s(step.icon) || i + 1}
							</Flex>
							<Heading size='sm'>{s(step.title)}</Heading>
							{Boolean(step.description) && (
								<Text fontSize='sm' color='gray.600'>
									{s(step.description)}
								</Text>
							)}
						</Stack>
					))}
				</SimpleGrid>
			</Container>
		</Box>
	)
}

function ComparisonTableSection({ config }: { config: Cfg }) {
	const features = arr<string>(config.features)
	const tiers = arr<{ name: string; highlighted: boolean; values: string[] }>(
		config.tiers
	)
	if (!features.length || !tiers.length) return null
	const box = sectionBoxProps(config, '6xl')
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				{Boolean(config.title) && (
					<Heading size='lg' mb={8} textAlign='center'>
						{s(config.title)}
					</Heading>
				)}
				{/* Wide tables must scroll inside their own box rather than
				    making the whole page scroll sideways on mobile. */}
				<Box overflowX='auto'>
					<Box as='table' width='100%' style={{ borderCollapse: 'collapse' }}>
						<Box as='thead'>
							<Box as='tr'>
								<Box as='th' textAlign='start' p={3} borderBottomWidth='1px' />
								{tiers.map((tier, i) => (
									<Box
										as='th'
										key={i}
										p={3}
										borderBottomWidth='1px'
										fontWeight='bold'
										bg={tier.highlighted ? 'primary.50' : undefined}
									>
										{s(tier.name)}
									</Box>
								))}
							</Box>
						</Box>
						<Box as='tbody'>
							{features.map((feature, r) => (
								<Box as='tr' key={r}>
									<Box as='td' p={3} borderBottomWidth='1px' fontSize='sm'>
										{s(feature)}
									</Box>
									{tiers.map((tier, c) => (
										<Box
											as='td'
											key={c}
											p={3}
											borderBottomWidth='1px'
											textAlign='center'
											fontSize='sm'
											bg={tier.highlighted ? 'primary.50' : undefined}
										>
											{/* Short `values` arrays are padded rather than
											    rejected, so adding a feature row does not
											    invalidate every existing tier. */}
											{s(arr<string>(tier.values)[r], '—')}
										</Box>
									))}
								</Box>
							))}
						</Box>
					</Box>
				</Box>
			</Container>
		</Box>
	)
}

function PricingTableSection({
	config,
	locale,
}: {
	config: Cfg
	locale: string
}) {
	const plans = arr<{
		name: string
		price: string
		period: string
		description: string
		features: string[]
		cta_text: string
		cta_form_code: string
		highlighted: boolean
	}>(config.plans)
	if (!plans.length) return null
	const box = sectionBoxProps(config, '6xl')
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				{Boolean(config.title) && (
					<Heading size='lg' mb={10} textAlign='center'>
						{s(config.title)}
					</Heading>
				)}
				<SimpleGrid
					columns={{
						base: 1,
						md: Math.min(plans.length, 2),
						lg: Math.min(plans.length, 4),
					}}
					spacing={6}
				>
					{plans.map((plan, i) => (
						<Stack
							key={i}
							spacing={4}
							p={6}
							borderWidth={plan.highlighted ? '2px' : '1px'}
							borderColor={plan.highlighted ? 'primary.500' : undefined}
							borderRadius='lg'
						>
							<Heading size='sm'>{s(plan.name)}</Heading>
							<Flex align='baseline' gap={1}>
								<Text fontSize='2xl' fontWeight='bold'>
									{s(plan.price)}
								</Text>
								{Boolean(plan.period) && (
									<Text fontSize='sm' color='gray.500'>
										/{s(plan.period)}
									</Text>
								)}
							</Flex>
							{Boolean(plan.description) && (
								<Text fontSize='sm' color='gray.600'>
									{s(plan.description)}
								</Text>
							)}
							<Stack spacing={2}>
								{arr<string>(plan.features).map((f, j) => (
									<Text key={j} fontSize='sm'>
										• {s(f)}
									</Text>
								))}
							</Stack>
							{Boolean(plan.cta_form_code) && (
								<Button
									as={NextLink}
									href={localeHref(locale, `/forms/${plan.cta_form_code}`)}
									colorScheme='primary'
									size='sm'
								>
									{s(plan.cta_text, 'Get a quote')}
								</Button>
							)}
						</Stack>
					))}
				</SimpleGrid>
			</Container>
		</Box>
	)
}

function DocumentDownloadsSection({ config }: { config: Cfg }) {
	const docs = arr<{
		label: string
		url: string
		file_type: string
		size_label: string
	}>(config.documents)
	if (!docs.length) return null
	const box = sectionBoxProps(config, '4xl')
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				{Boolean(config.title) && (
					<Heading size='lg' mb={6}>
						{s(config.title)}
					</Heading>
				)}
				<Stack spacing={3}>
					{docs.map((doc, i) => (
						<Flex
							key={i}
							as='a'
							href={s(doc.url) || undefined}
							target='_blank'
							rel='noopener noreferrer'
							align='center'
							justify='space-between'
							p={4}
							borderWidth='1px'
							borderRadius='md'
							_hover={{ bg: 'gray.50' }}
						>
							<Text fontWeight='medium'>{s(doc.label)}</Text>
							<Text fontSize='xs' color='gray.500'>
								{[s(doc.file_type).toUpperCase(), s(doc.size_label)]
									.filter(Boolean)
									.join(' · ')}
							</Text>
						</Flex>
					))}
				</Stack>
			</Container>
		</Box>
	)
}

function TrustBadgesSection({
	config,
	locale,
}: {
	config: Cfg
	locale: string
}) {
	const badges = arr<{ label: string; image_url: string; link_url: string }>(
		config.badges
	)
	const height = Number(config.badge_height_px ?? 56)
	if (!badges.length) return null
	const box = sectionBoxProps(config, '5xl', 10)
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				{Boolean(config.title) && (
					<Text mb={6} textAlign='center' fontSize='sm' color='gray.500'>
						{s(config.title)}
					</Text>
				)}
				<Flex wrap='wrap' align='center' justify='center' gap={8}>
					{badges.map((badge, i) => {
						const img = (
							<Image
								src={badge.image_url}
								alt={s(badge.label)}
								height={`${height}px`}
								objectFit='contain'
							/>
						)
						return badge.link_url ? (
							<NextLink
								key={i}
								href={localeHref(locale, badge.link_url)}
								target='_blank'
								rel='noopener noreferrer'
							>
								{img}
							</NextLink>
						) : (
							<Box key={i}>{img}</Box>
						)
					})}
				</Flex>
			</Container>
		</Box>
	)
}

function TeamGridSection({ config }: { config: Cfg }) {
	const people = arr<{
		name: string
		role: string
		photo_url: string
		bio: string
	}>(config.people)
	const columns = Math.min(Math.max(Number(config.columns ?? 3), 1), 4)
	if (!people.length) return null
	const box = sectionBoxProps(config, '6xl')
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				{Boolean(config.title) && (
					<Heading size='lg' mb={10} textAlign='center'>
						{s(config.title)}
					</Heading>
				)}
				<SimpleGrid columns={{ base: 1, sm: 2, lg: columns }} spacing={8}>
					{people.map((person, i) => (
						<Stack key={i} spacing={3} align='center' textAlign='center'>
							{person.photo_url ? (
								<Image
									src={person.photo_url}
									alt={s(person.name)}
									boxSize='120px'
									borderRadius='full'
									objectFit='cover'
								/>
							) : (
								/* Placeholder rather than a broken image — a missing
								   headshot is normal, not an error state. */
								<Flex
									boxSize='120px'
									borderRadius='full'
									bg='gray.100'
									align='center'
									justify='center'
									fontSize='2xl'
									color='gray.400'
								>
									{s(person.name).slice(0, 1).toUpperCase() || '?'}
								</Flex>
							)}
							<Stack spacing={0}>
								<Heading size='sm'>{s(person.name)}</Heading>
								{Boolean(person.role) && (
									<Text fontSize='sm' color='gray.500'>
										{s(person.role)}
									</Text>
								)}
							</Stack>
							{Boolean(person.bio) && (
								<Text fontSize='sm' color='gray.600'>
									{s(person.bio)}
								</Text>
							)}
						</Stack>
					))}
				</SimpleGrid>
			</Container>
		</Box>
	)
}

/** YouTube/Vimeo watch URLs -> their embed form. Studio already rejects any
 *  other host at save time; this only reshapes an allowed URL. */
function toEmbedUrl(raw: string): string {
	if (!raw) return ''
	try {
		const u = new URL(raw)
		const host = u.hostname.replace(/^www\./, '')
		if (host === 'youtu.be') return `https://www.youtube.com/embed${u.pathname}`
		if (host === 'youtube.com') {
			const v = u.searchParams.get('v')
			if (v) return `https://www.youtube.com/embed/${v}`
			if (u.pathname.startsWith('/embed/')) return raw
		}
		if (host === 'vimeo.com')
			return `https://player.vimeo.com/video${u.pathname}`
		if (host === 'player.vimeo.com') return raw
		return ''
	} catch {
		return ''
	}
}

function VideoEmbedSection({ config }: { config: Cfg }) {
	const src = toEmbedUrl(s(config.video_url))
	if (!src) return null
	const box = sectionBoxProps(config, '4xl')
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				{Boolean(config.title) && (
					<Heading size='lg' mb={6} textAlign='center'>
						{s(config.title)}
					</Heading>
				)}
				<Box
					position='relative'
					width='100%'
					paddingTop='56.25%'
					borderRadius='lg'
					overflow='hidden'
				>
					<Box
						as='iframe'
						src={src}
						title={s(config.title, 'Video')}
						allow='accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture'
						allowFullScreen
						position='absolute'
						top={0}
						left={0}
						width='100%'
						height='100%'
						border='0'
					/>
				</Box>
				{Boolean(config.caption) && (
					<Text mt={3} fontSize='sm' color='gray.500' textAlign='center'>
						{s(config.caption)}
					</Text>
				)}
			</Container>
		</Box>
	)
}

function AppDownloadSection({ config }: { config: Cfg }) {
	const ios = s(config.ios_url)
	const android = s(config.android_url)
	if (!ios && !android) return null
	const box = sectionBoxProps(config, '4xl')
	return (
		<Box
			id={box.id}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
			bg={box.bg || 'gray.50'}
		>
			<Container maxW={box.maxW}>
				<Flex
					direction={{ base: 'column', md: 'row' }}
					align='center'
					justify='space-between'
					gap={6}
				>
					<Stack spacing={2}>
						{Boolean(config.title) && (
							<Heading size='md'>{s(config.title)}</Heading>
						)}
						{Boolean(config.subtitle) && (
							<Text color='gray.600'>{s(config.subtitle)}</Text>
						)}
						<Flex gap={3} wrap='wrap' pt={2}>
							{/* Only render a store button when that store's URL is
							    configured — never a dead link for the missing one. */}
							{Boolean(ios) && (
								<Button
									as='a'
									href={ios}
									target='_blank'
									rel='noopener noreferrer'
									size='sm'
								>
									App Store
								</Button>
							)}
							{Boolean(android) && (
								<Button
									as='a'
									href={android}
									target='_blank'
									rel='noopener noreferrer'
									size='sm'
								>
									Google Play
								</Button>
							)}
						</Flex>
					</Stack>
					{Boolean(config.qr_image_url) && (
						<Image
							src={s(config.qr_image_url)}
							alt='Download QR code'
							boxSize='120px'
							objectFit='contain'
						/>
					)}
				</Flex>
			</Container>
		</Box>
	)
}

function BranchLocatorSection({ config }: { config: Cfg }) {
	const branches = arr<{
		name: string
		address: string
		phone: string
		hours: string
	}>(config.branches)
	const columns = Math.min(Math.max(Number(config.columns ?? 2), 1), 4)
	if (!branches.length) return null
	const box = sectionBoxProps(config, '5xl')
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				{Boolean(config.title) && (
					<Heading size='lg' mb={8}>
						{s(config.title)}
					</Heading>
				)}
				<SimpleGrid columns={{ base: 1, md: columns }} spacing={6}>
					{branches.map((branch, i) => (
						<Stack
							key={i}
							spacing={2}
							p={5}
							borderWidth='1px'
							borderRadius='lg'
						>
							<Heading size='sm'>{s(branch.name)}</Heading>
							{Boolean(branch.address) && (
								<Text fontSize='sm' color='gray.600'>
									{s(branch.address)}
								</Text>
							)}
							{Boolean(branch.phone) && (
								<Text fontSize='sm'>
									<Box as='a' href={`tel:${s(branch.phone)}`} color='blue.600'>
										{s(branch.phone)}
									</Box>
								</Text>
							)}
							{Boolean(branch.hours) && (
								<Text fontSize='xs' color='gray.500'>
									{s(branch.hours)}
								</Text>
							)}
							{Boolean(branch.address) && (
								/* No map library is installed, and adding one is a
								   provider/API-key/cost decision of its own. Handing
								   the visitor off to their own maps app covers the
								   actual need without that dependency. */
								<Box
									as='a'
									href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
										s(branch.address)
									)}`}
									target='_blank'
									rel='noopener noreferrer'
									fontSize='sm'
									color='blue.600'
									pt={1}
								>
									Get directions
								</Box>
							)}
						</Stack>
					))}
				</SimpleGrid>
			</Container>
		</Box>
	)
}

function CustomHtmlSection({ config }: { config: Cfg }) {
	const html = s(config.html)
	if (!html) return null
	const box = sectionBoxProps(config, '5xl', 8)
	return (
		<Box
			id={box.id}
			bg={box.bg}
			color={box.color}
			px={{ base: 4, sm: 8 }}
			py={box.py}
			pt={box.pt}
			pb={box.pb}
			textAlign={box.textAlign}
		>
			<Container maxW={box.maxW}>
				{/* Sanitized server-side on save by the same allowlist that guards
				    Article.body (html_sanitizer.py). Studio is the security
				    boundary — do not add a client-side sanitizer here, and do not
				    route any other HTML string through this path. */}
				<Box dangerouslySetInnerHTML={{ __html: html }} />
			</Container>
		</Box>
	)
}

// --- Dispatcher ------------------------------------------------------------
export function renderPageSection(
	section: StudioPageSection,
	product: Record<string, unknown> | null,
	locale: string,
	/** Articles pre-fetched server-side for a `blog_listing` section, keyed by
	 *  section id. The dispatcher is synchronous, so a section that needs
	 *  remote data has to receive it rather than fetch it. */
	articlesBySection: Record<string, StudioArticleCard[]> = {},
	/** Products resolved server-side from a tenant component's stored product
	 *  CODES, keyed by section id. Same reasoning as articlesBySection — and
	 *  the reason codes are stored rather than copies of product data
	 *  (BYOD plan Phase 8): a repriced product must not need every page that
	 *  mentions it re-edited. */
	productsBySection: Record<string, ResolvedTenantProduct[]> = {}
): React.ReactNode {
	const config = section.config || {}

	// Handled ahead of the switch, not inside it. `tenant_component` is a real
	// Studio section type but STUDIO_PAGE_SECTION_TYPES in the released
	// @crosure/tenant-runtime does not list it yet, and adding it there trips
	// that module's exhaustiveness assert against STUDIO_PAGE_COMPONENTS,
	// which expects a fixed schema per type — a tenant component has none by
	// definition. Keeping it out of the switch preserves the `never` check
	// below, which is what forces a NEW built-in type to be handled.
	if ((section.section_type as string) === 'tenant_component') {
		return (
			<TenantComponentSection
				key={section.id}
				config={config}
				products={productsBySection[section.id] ?? []}
			/>
		)
	}

	switch (section.section_type) {
		case 'hero':
			return <HeroSection key={section.id} config={config} locale={locale} />
		case 'markdown':
			return <MarkdownSection key={section.id} config={config} />
		case 'cta':
			return <CtaSection key={section.id} config={config} locale={locale} />
		case 'stats':
			return <StatsSection key={section.id} config={config} />
		case 'separator':
			return <SeparatorSection key={section.id} config={config} />
		case 'faq':
			return <FaqSection key={section.id} config={config} />
		case 'testimonials':
			return <TestimonialsSection key={section.id} config={config} />
		case 'image_text':
			return (
				<ImageTextSection key={section.id} config={config} locale={locale} />
			)
		case 'logos':
			return <LogosSection key={section.id} config={config} locale={locale} />
		case 'product_showcase':
			return (
				<ProductShowcaseSection
					key={section.id}
					config={config}
					product={product}
					locale={locale}
				/>
			)
		case 'coverage_grid':
			return (
				<CoverageGridSection
					key={section.id}
					config={config}
					product={product}
				/>
			)
		case 'form_embed':
			return (
				<FormEmbedSection key={section.id} config={config} locale={locale} />
			)
		case 'how_it_works':
			return <HowItWorksSection key={section.id} config={config} />
		case 'comparison_table':
			return <ComparisonTableSection key={section.id} config={config} />
		case 'pricing_table':
			return (
				<PricingTableSection key={section.id} config={config} locale={locale} />
			)
		case 'document_downloads':
			return <DocumentDownloadsSection key={section.id} config={config} />
		case 'trust_badges':
			return (
				<TrustBadgesSection key={section.id} config={config} locale={locale} />
			)
		case 'team_grid':
			return <TeamGridSection key={section.id} config={config} />
		case 'video_embed':
			return <VideoEmbedSection key={section.id} config={config} />
		case 'app_download':
			return <AppDownloadSection key={section.id} config={config} />
		case 'branch_locator':
			return <BranchLocatorSection key={section.id} config={config} />
		case 'custom_html':
			return <CustomHtmlSection key={section.id} config={config} />
		case 'blog_listing':
			return (
				<BlogListingSection
					key={section.id}
					config={config}
					articles={articlesBySection[section.id] ?? []}
					locale={locale}
				/>
			)
	}

	const unsupportedSection: never = section.section_type
	return unsupportedSection
}
